/**
 * Choose what the inline player plays, and describe it in the player's terms.
 *
 * Three candidates, in priority order: a clip the viewer picked, the live
 * stream, the most recent past broadcast. Returning `null` means "nothing
 * playable" and the panel shows its poster state.
 *
 * A pure function, split out of `StreamPanel.tsx` — it reads only its inputs,
 * which makes the precedence readable in one screen and got that file back
 * under the 400-line ceiling.
 */
import type { FeaturedStreamResponse } from "@/hooks/hub/useGetHub";
import type { InlinePlatform, InlinePlayerSource } from "../StreamInlinePlayer";
import type { SelectedClip } from "../shared";
import type { HeroFallback } from "./useHeroFallback";
import {
    extractTwitchClipSlug, extractTwitchVideoId, extractYouTubeVideoId,
} from "./streamSourceParsing";

export interface InlineSourceInput {
    isShowingClip: boolean;
    selectedClip?: SelectedClip | null;
    effectiveStreamLive: boolean;
    effectiveStream: FeaturedStreamResponse | null;
    /** The live stream's platform, already narrowed to one the player can embed. */
    embeddablePlatform?: InlinePlatform;
    heroFallback: HeroFallback | null;
    availablePlatforms: FeaturedStreamResponse[];
    hubName?: string;
    userInitiatedPlay: boolean;
    surfaceId?: string;
}

export function buildInlineSource(input: InlineSourceInput): InlinePlayerSource | null {
    const {
        isShowingClip, selectedClip, effectiveStreamLive, effectiveStream,
        embeddablePlatform, heroFallback, availablePlatforms, hubName,
        userInitiatedPlay, surfaceId,
    } = input;

    if (isShowingClip && selectedClip) {
        const cfg = availablePlatforms.find(p => p.platform === selectedClip.source);
        /*
         * For Kick clips specifically: if hlsUrl is missing the VOD has no
         * inline-playable source. We must NOT fall through to the live
         * channel iframe (StreamInlinePlayer would otherwise pick up
         * `kickChannelSlug` and play the live stream / show offline screen
         * — silent wrong-content). Surface the empty state instead by
         * dropping the kickChannelSlug.
         */
        const kickInlineMissing = selectedClip.source === "KICK" && !selectedClip.hlsUrl;
        return {
            platform: selectedClip.source as InlinePlayerSource["platform"],
            twitchChannelId: cfg?.twitchChannelId,
            youtubeChannelId: cfg?.youtubeChannelId,
            youtubeLiveId: cfg?.youtubeLiveId,
            kickChannelSlug: kickInlineMissing ? undefined : cfg?.kickChannelSlug,
            isLive: false,
            twitchClipSlug: selectedClip.source === "TWITCH" ? extractTwitchClipSlug(selectedClip.url) : undefined,
            twitchVideoId: selectedClip.source === "TWITCH" ? extractTwitchVideoId(selectedClip.url) : undefined,
            youtubeVideoId: selectedClip.source === "YOUTUBE" ? extractYouTubeVideoId(selectedClip.url) : undefined,
            kickHlsUrl: selectedClip.source === "KICK" ? selectedClip.hlsUrl : undefined,
            title: selectedClip.title,
            muted: false,
        };
    }

    /*
     * `embeddablePlatform` is checked, not cast.
     *
     * It was `effectiveStream.platform as InlinePlayerSource["platform"]`,
     * asserting a required three-valued type over an optional four-valued
     * source. That is the same assertion that hid the ChallengesSection crash
     * on this branch — the cast silences the mismatch and `undefined` flows on
     * to code that assumes a platform.
     *
     * A stream with no single platform cannot be played at all: the embed
     * builder switches on it and has nothing to build. Returning no source is
     * the honest answer, and the panel already renders its poster state when
     * there is none.
     */
    if (effectiveStreamLive && effectiveStream && embeddablePlatform) {
        return {
            platform: embeddablePlatform,
            twitchChannelId: effectiveStream.twitchChannelId,
            youtubeChannelId: effectiveStream.youtubeChannelId,
            youtubeLiveId: effectiveStream.youtubeLiveId,
            kickChannelSlug: effectiveStream.kickChannelSlug,
            isLive: true,
            title: `${hubName ?? "Creator"} live`,
            /* muted=true so browser autoplay policy doesn't block the auto-mount on initial render */
            muted: true,
            viewerInitiated: userInitiatedPlay,
            surfaceId,
        };
    }

    if (heroFallback) {
        const cfg = availablePlatforms.find(p => p.platform === heroFallback.source);
        return {
            platform: heroFallback.source,
            twitchChannelId: cfg?.twitchChannelId,
            youtubeChannelId: cfg?.youtubeChannelId,
            youtubeLiveId: cfg?.youtubeLiveId,
            kickChannelSlug: cfg?.kickChannelSlug,
            isLive: false,
            twitchClipSlug: heroFallback.source === "TWITCH" ? extractTwitchClipSlug(heroFallback.url) : undefined,
            twitchVideoId: heroFallback.source === "TWITCH" ? extractTwitchVideoId(heroFallback.url) : undefined,
            youtubeVideoId: heroFallback.source === "YOUTUBE" ? extractYouTubeVideoId(heroFallback.url) : undefined,
            kickHlsUrl: heroFallback.source === "KICK" ? heroFallback.hlsUrl : undefined,
            title: heroFallback.title,
            muted: false,
        };
    }

    return null;
}
